import React from "react";

import { Box, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";

import {
  CaretDownIcon,
  CheckIcon,
  ClockIcon,
  EyeIcon,
  PaperPlaneTiltIcon,
} from "@phosphor-icons/react/dist/ssr";

interface ListCardHeaderProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  isApproved: boolean;
  onOpenModal?: () => void;
  status?: string;
  isRead?: boolean;
  appearance?: "default" | "example";
}

const ListCardHeader: React.FC<ListCardHeaderProps> = ({
  isOpen,
  setIsOpen,
  title,
  isApproved,
  onOpenModal,
  status,
  isRead,
  appearance = "default",
}) => {
  const theme = useTheme();
  const isExampleAppearance = appearance === "example";
  const getStatusColor = () => {
    return {
      color: "text.primary",
    };
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "complete":
        return <CheckIcon size={16} weight="bold" color={theme.palette.text.primary} />;
      case "incomplete":
        return <ClockIcon size={16} weight="bold" color={theme.palette.text.primary} />;
      case "sent":
        return (
          <PaperPlaneTiltIcon
            size={16}
            weight="bold"
            color={theme.palette.text.primary}
          />
        );
      case "pending":
        return <ClockIcon size={16} weight="bold" color={theme.palette.text.primary} />;
      default:
        return <CheckIcon size={16} weight="bold" color={theme.palette.text.primary} />;
    }
  };

  return (
    <Box
      onClick={() => setIsOpen(!isOpen)}
      sx={{
        px: isExampleAppearance ? 1.5 : 1.5,
        py: isExampleAppearance ? 1 : 1,
        backgroundColor: isExampleAppearance ? "transparent" : "primary.light",
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        borderBottom: isExampleAppearance ? "1px solid rgba(28, 72, 46, 0.45)" : "none",
        transition: "background-color 0.3s",
      }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography
          variant={isExampleAppearance ? "text14B" : "subtitle2"}
          sx={{
            color: isExampleAppearance
              ? isApproved
                ? "secondary.main"
                : "text.primary"
              : isApproved
                ? "green.500"
                : "blue.500",
            flex: 1,
            letterSpacing: 0,
            textTransform: "none",
          }}
        >
          {title}
        </Typography>
        {status && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{
              px: 0,
              py: 0,
              borderRadius: 0,
              border: "none",
              backgroundColor: "transparent",
            }}
          >
            {getStatusIcon(status)}
            {!isExampleAppearance && (
              <Typography
                variant="subtitle2"
                sx={{
                  ...getStatusColor(),
                  textTransform: "none",
                }}
              >
                {status}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap={1}>
        {!isRead && (
          <Chip
            label="NEW"
            color="primary"
            size="small"
            sx={
              isExampleAppearance
                ? {
                  backgroundColor: "transparent",
                  color: "primary.main",
                  border: "1px solid",
                  borderColor: "primary.main",
                  fontWeight: 500,
                }
                : undefined
            }
          />
        )}

        {onOpenModal && (
          <Tooltip title="View Details" arrow>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onOpenModal();
              }}
              size="small"
              sx={
                isExampleAppearance
                  ? {
                    p: 0.5,
                    color: "text.primary",
                    "&:hover": {
                      backgroundColor: "rgba(28, 72, 46, 0.08)",
                    },
                  }
                  : undefined
              }
            >
              <EyeIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box
          sx={{
            width: "auto",
            height: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: 0,
          }}
        >
          <CaretDownIcon
            size={16}
            weight="bold"
            style={{
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
            }}
            color={isExampleAppearance ? theme.palette.secondary.main : undefined}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default ListCardHeader;
